import { useState } from 'react';
import { Link } from 'react-router-dom';
import { CustomerTypeButton } from '../../components/Header/CustomerTypeSwitch/CustomerTypeSwitch';
import { Icon, caret_down_footer, caret_up_footer } from '../../components/Icon/Icon';
import { useConfig } from '../../config';
import { useTranslation } from '../../i18n/i18n';
import { CategoryTreeContainer } from '../../state/categories';
import './Footer.css';
import { ondioColumn } from './ondioColumn';

export const FooterLists = (): JSX.Element => {
	const footerTrans = useTranslation().footer;
	const navigationItemsTrans = useTranslation().navigation;
	const routes = useTranslation().routes;
	const categories = CategoryTreeContainer.useContainer().tree.children;
	const config = useConfig();

	const brandItems = config.footerBrandItems.map((item) => (
		<Link key={item.url} to={item.url}>
			{item.name}
		</Link>
	));

	const categoriesLinks = categories.filter((c) => !c.hide).map((c) => <Link to={'/' + c.perma}>{c.name}</Link>);

	const listItems = {
		departments: [
			<Link to={routes.productsA}>{navigationItemsTrans.productsAo}</Link>,
			<Link to={routes.brands}>{navigationItemsTrans.brands}</Link>,
			config.store === 'kgse' || config.store === 'kgno' ? (
				<Link to={routes.homeOffice}>{navigationItemsTrans.homeOffice}</Link>
			) : null,
			<Link to={routes.campaigns}>{navigationItemsTrans.campaigns}</Link>,
		],
		shopHere: [
			<Link to={routes.account}>{navigationItemsTrans.account}</Link>,
			['kgse', 'kgnl', 'kgdk', 'kgde', 'kgno'].includes(config.store) ? (
				<Link to={routes.faq}>{footerTrans.faq}</Link>
			) : null,
			<Link to={routes.purchaseTerms}>{footerTrans.purchaseTerms}</Link>,
			['kgse', 'kgno', 'kgdk', 'kgnl'].includes(config.store) ? (
				<Link to={routes.sustainability}>{footerTrans.sustainabilityIso}</Link>
			) : null,
			<Link to={routes.bonus}>{footerTrans.becomeBonusCustomer}</Link>,
			<Link to={routes.return}>{footerTrans.returns}</Link>,
			<Link to={routes.privacyPolicy}>{footerTrans.privacyPolicy}</Link>,
			<ToggleConsentSettingsPopupListItem text={footerTrans.cookies} />,
			config.store !== 'kgse' ? (
				<a href={config.klarnaFaqLink} target="_blank">
					{footerTrans.klarnaFaq}
				</a>
			) : null,
			<Link to={routes.about}>{footerTrans.about}</Link>,
			config.store === 'kgde' || config.store === 'kgat' ? (
				<Link to={routes.impressum}>{footerTrans.impressum}</Link>
			) : null,
		],
		categories: [
			...categoriesLinks,
			<Link to={routes.productsA}>{navigationItemsTrans.productsAo}</Link>,
			config.store === 'kgse' || config.store === 'kgno' ? (
				<Link to={routes.homeOffice}>{navigationItemsTrans.homeOffice}</Link>
			) : null,
			<Link to={routes.campaigns}>{navigationItemsTrans.campaigns}</Link>,
		],
		brands: brandItems,
	};

	const ondio = ondioColumn(config.store, config.logo);

	return (
		<div className={`lists ${ondio ? 'with-ondio' : ''}`}>
			<List heading={footerTrans.shopHere} items={listItems.shopHere} />
			<List heading={footerTrans.categories} items={categoriesLinks} className="only-mobile" />
			<List heading={footerTrans.departments} open={true} items={listItems.departments} className="only-mobile" />
			<List heading={footerTrans.categories} items={listItems.categories} className="not-mobile" />
			<List heading={footerTrans.brands} items={brandItems} className="not-mobile" />
			{ondio && <List heading={ondio.heading} items={ondio.items} className="ondio" />}
		</div>
	);
};

/**
 * Customer service and the customer type switch, which on desktop sit under the logo rather than
 * taking a column of their own among the lists. Mobile has its own customer service block at the
 * top of the footer, so this one is desktop only.
 */
export const FooterServiceColumn = (): JSX.Element => {
	const footerTrans = useTranslation().footer;
	const config = useConfig();

	const customerService = [
		<a href={`mailto:${config.customerServiceEmail}`}>{config.customerServiceEmail}</a>,
		<a href={`tel:${config.customerServicePhoneNumber}`}>{config.customerServicePhoneNumber}</a>,
		footerTrans.openHours,
	];
	const shopAs = [
		<span>
			<CustomerTypeButton />
		</span>,
	];

	return (
		<div className="service-column not-mobile">
			<List heading={footerTrans.customerService} items={customerService} />
			<List heading={footerTrans.shopAs} items={shopAs} className="buy-as" />
		</div>
	);
};

type ListProps = {
	heading: string;
	items: (string | JSX.Element | null)[];
	className?: string;
	open?: boolean;
};
const List = ({ items, heading, className, open }: ListProps): JSX.Element => {
	const [isOpen, setIsOpen] = useState(open ?? false);
	return (
		<div className={`list ${className ? className : ''} ${isOpen ? 'open' : ''}`}>
			<p className="heading" onClick={() => setIsOpen(!isOpen)}>
				{heading}
			</p>
			<span onClick={() => setIsOpen(!isOpen)}>
				<Icon shape={isOpen ? caret_up_footer : caret_down_footer} className="only-mobile" />
			</span>
			<ul className="list">
				{items.map((item, i) => (
					<li key={heading + i}>{item}</li>
				))}
			</ul>
		</div>
	);
};

const ToggleConsentSettingsPopupListItem = ({ text }: { text: string }): JSX.Element => {
	const openConsentSettings = () => {
		if (typeof window === 'undefined') return;

		const cookiebotWidget = document.getElementById('CookiebotWidget');
		if (cookiebotWidget?.classList.contains('CookiebotWidget-open')) return;

		const cookiebotToggleButton = cookiebotWidget?.querySelector<HTMLButtonElement>(
			'button.CookiebotWidget-logo, button'
		);

		if (cookiebotToggleButton) {
			cookiebotToggleButton.dispatchEvent(
				new MouseEvent('click', {
					bubbles: true,
					cancelable: true,
					view: window,
				})
			);
			return;
		}

		window.Cookiebot?.renew?.();
	};

	return (
		<a className="cookie-consent-toggle-item" onClick={openConsentSettings}>
			{text}
		</a>
	);
};
